import { useParams, Link } from 'react-router-dom';
import { useLocale, useT, withLocale } from '../i18n';
import { useApiData } from '../api/useApiData';
import { contentApi } from '../api/content';
import AsyncSection from '../components/shared/AsyncSection';
import { usePageContentId } from '../components/lisa/PageContext';

export default function SolutionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const locale = useLocale();
  const t = useT();
  // Solutions are fetched by code in the API, but routed by slug — one
  // extra list call resolves slug -> code; small and cached-by-the-list-
  // call-the-header-already-makes in practice.
  const listState = useApiData(() => contentApi.solutions(locale), [locale]);
  const code = listState.status === 'ready' ? listState.data.find((s) => s.slug === slug)?.code : undefined;
  const detailState = useApiData(() => (code ? contentApi.solution(code, locale) : Promise.reject(new Error('not resolved yet'))), [code, locale]);

  usePageContentId(code ? `solution:${code}` : null);

  if (listState.status === 'loading' || (listState.status === 'ready' && !code && detailState.status === 'loading')) {
    return (
      <section className="section" style={{ paddingTop: 160 }}>
        <div className="container">
          <p className="body-sm">Loading…</p>
        </div>
      </section>
    );
  }
  if (listState.status === 'ready' && !code) {
    return (
      <section className="section" style={{ paddingTop: 160 }}>
        <div className="container">
          <p className="body-sm">This solution isn't published yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="section" style={{ paddingTop: 160 }}>
      <div className="container">
        <AsyncSection
          state={detailState}
          render={(s) => (
            <>
              <div className="reveal is-visible">
                <Link to={withLocale('/', locale)}>{t('common.home')}</Link> / <Link to={withLocale('/solutions', locale)}>{t('solutions.eyebrow')}</Link> / {s.name}
              </div>
              <span className="badge badge-core" style={{ marginTop: 16, display: 'inline-block' }}>
                {s.classification}
              </span>
              <h1 style={{ margin: '16px 0 8px' }}>
                {s.code} — {s.name}
              </h1>
              {s.externalUrl && (
                <div style={{ marginTop: 20, marginBottom: 8 }}>
                  <a
                    href={`${s.externalUrl}/${locale}/index.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '14px 28px',
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #5B9BFF 0%, #C9A227 100%)',
                      color: '#0B1B33',
                      textDecoration: 'none',
                      boxShadow: '0 4px 20px rgba(91, 155, 255, 0.4)',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem' }}>🛡️</span>
                    <span>
                      {locale === 'ar'
                        ? 'زيارة الموقع المخصص لـ CYRKIL SHIELD ↗'
                        : locale === 'fr'
                        ? 'Visiter le site dédié CYRKIL SHIELD ↗'
                        : 'Visit Dedicated CYRKIL SHIELD Website ↗'}
                    </span>
                  </a>
                </div>
              )}

              <div className="card-grid" style={{ marginTop: 48, gridTemplateColumns: '1fr 1fr' }}>
                <div className="card reveal is-visible">
                  <span className="eyebrow">{t('solutions.problem')}</span>
                  <p className="body-lg" style={{ marginTop: 8 }}>
                    {s.problem}
                  </p>
                </div>
                <div className="card reveal is-visible">
                  <span className="eyebrow">{t('solutions.outcome')}</span>
                  <p className="body-lg" style={{ marginTop: 8 }}>
                    {s.outcome}
                  </p>
                </div>
              </div>

              <div className="section-head" style={{ marginTop: 64 }}>
                <span className="eyebrow">{t('solutions.approach_eyebrow')}</span>
                <h2>{t('solutions.approach_headline')}</h2>
                <p className="body-lg">{s.approach}</p>
              </div>

              <div className="section-head" style={{ marginTop: 16 }}>
                <span className="eyebrow">{t('solutions.capabilities_eyebrow')}</span>
                <h2>{t('solutions.capabilities_headline')}</h2>
              </div>
              <div className="card-grid">
                {s.capabilities.map((c) => (
                  <div className="card reveal is-visible" key={c}>
                    <h3 style={{ fontSize: '1rem' }}>{c}</h3>
                  </div>
                ))}
              </div>
              <p className="body-sm" style={{ marginTop: 16 }}>
                <Link to={withLocale('/contact', locale) + '?purpose=technical_discussion'} style={{ color: 'var(--accent-highlight)' }}>
                  {t('common.request_full_register')}
                </Link>
              </p>

              <div className="section-head" style={{ marginTop: 64 }}>
                <span className="eyebrow">{t('solutions.stages_eyebrow')}</span>
                <h2>{t('solutions.stages_headline')}</h2>
              </div>
              <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                {s.methodologyStageTags.map((stage) => (
                  <Link key={stage} className="chip-filter" to={withLocale(`/methodology#${stage}`, locale)}>
                    {stage[0].toUpperCase() + stage.slice(1)}
                  </Link>
                ))}
              </div>

              <div className="card-grid" style={{ marginTop: 64, gridTemplateColumns: '1fr 1fr' }}>
                <div>
                  <h3>{t('solutions.related_industries')}</h3>
                  <div className="flex gap-3" style={{ flexWrap: 'wrap', marginTop: 12 }}>
                    {s.relatedIndustries.map((i) => (
                      <Link key={i.slug} className="card reveal is-visible" style={{ padding: '16px 20px' }} to={withLocale(`/industries/${i.slug}`, locale)}>
                        {i.name}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <h3>{t('solutions.related_platform')}</h3>
                  <p style={{ marginTop: 12 }}>
                    {s.relatedPlatformModules.map((m) => (
                      <span key={m.code} className="badge badge-platform" style={{ margin: '4px 6px 0 0' }}>
                        {m.name}
                      </span>
                    ))}
                  </p>
                </div>
              </div>

              <div className="card evidence-tile reveal is-visible" style={{ marginTop: 64 }}>
                <div className="card-icon">◆</div>
                <div>
                  <h3>{t('solutions.evidence_title')}</h3>
                  <p className="body-sm">{t('solutions.evidence_body')}</p>
                </div>
              </div>

              <div className="card cta-band reveal is-visible" style={{ marginTop: 64 }}>
                <h2>
                  {t('solutions.cta_ready')} {s.code}?
                </h2>
                <div className="hero-cta-row" style={{ justifyContent: 'center', marginTop: 16, flexWrap: 'wrap', gap: 12 }}>
                  {s.externalUrl && (
                    <a
                      href={`${s.externalUrl}/${locale}/index.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-lg"
                      style={{
                        background: 'linear-gradient(135deg, #5B9BFF 0%, #C9A227 100%)',
                        color: '#0B1B33',
                        fontWeight: 700,
                      }}
                    >
                      🛡️ {locale === 'ar' ? 'فتح موقع CYRKIL SHIELD ↗' : locale === 'fr' ? 'Ouvrir le site CYRKIL SHIELD ↗' : 'Open CYRKIL SHIELD Website ↗'}
                    </a>
                  )}
                  <Link className="btn btn-secondary btn-lg" to={withLocale('/assessment', locale)}>
                    {t('solutions.cta_start_assessment')}
                  </Link>
                  <button className="btn btn-secondary btn-lg" type="button" data-open-lisa>
                    {t('common.talk_to_lisa')}
                  </button>
                </div>
              </div>
            </>
          )}
        />
      </div>
    </section>
  );
}
